import { STU_QUERY_KEYS, STU_TIMEOUT_VALUES, STU_TTL_VALUES } from "@/constants/stuQueryKeys";
import { IStuAffirmRequest, IStuAffirmResponse, IStuCommitRequest, IStuCreateQuoteResponse, IStuJourneyList, IStuKycAcceptPriceRequest, IStuKycAcceptPriceResponse, IStuKycForm, IStuKycInitiateRequest, IStuKycInitiateResponse, IStuKycStateResponse, IStuKycVerifyOtpRequest, IStuPaymentInitiateRequest, IStuPaymentInitiateResponse, IStuPaymentStatus, IStuPaymentSummary, IStuIfscLookup, IStuIfscResponse, IStuPincodeLookup, IStuPincodeResponse, IStuPolicyDocument, IStuProposalForm, IStuQuoteRequest, IStuQuoteResponse, IStuSaveAnswersRequest, IStuSaveAnswersResponse, IStuUploadDocumentRequest, IStuUploadDocumentResponse, IStuSelection, IStuSelectionRequest, IStuSubmitRequest, IStuSubmitResponse } from '@/types/stu';
import { ssoAxiosInstance } from "@/utils";
import {
  isTransientError,
  retryOnTransientError,
} from "@/utils/axios/baseAxiosInstance";
import employeeExperienceV1AxiosInstance from "@/utils/axios/employeeExperienceV1AxiosInstance";
import { isStuQuoteExpired, isStuQuoteLocked } from "@/utils/stuError";

/**
 * The super top-up journey's own backend mount (emp-exp `modules/superTopup`), behind the
 * `super-top-up` relay namespace. Every call this journey makes goes here — the Superleap lead
 * and opportunity included, filed under this line's own CRM product. Never the retail mount.
 */
export const STU_JOURNEY_API_BASE = '/relay/super-top-up/journey';

export class QuoteApiError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'QuoteApiError';
    this.code = code;
  }
}

export interface IProps {
  name: string;
  phone?: string;
  email?: string;
  productId?: string;
  query?: string;
  source?: string;
  isOffline?: boolean;
  info?: Record<string, unknown>;
  callbackRequested?: boolean;
  callbackMethod?: string;
  pincode?: string;
}

export const createLeadQuery = () => {
  return {
    mutationKey: [STU_QUERY_KEYS.CREATE_LEAD],
    mutationFn: async (requestBody: IProps) => {
      const url = `crm/leads`;
      const response = await employeeExperienceV1AxiosInstance.post(url, requestBody);

      return response.data.data;
    },
  };
};

export const updateLead = () => {
  return {
    mutationKey: [STU_QUERY_KEYS.UPDATE_LEAD],
    mutationFn: async (leadId: string) => {
      const url = `crm/leads/${leadId}`;
      const response = await employeeExperienceV1AxiosInstance.patch(url, { callbackRequested: true });
      return response.data;
    },
  };
};

export const getConfig = (
) => {
  return {
    queryKey: [STU_QUERY_KEYS.GET_CONFIG],
    queryFn: async () => {
      const url = `/rhi/config`;
      const response = await employeeExperienceV1AxiosInstance.get(url);
      return response.data;
    },
  };
};

export const createStuQuote = () => {
  return {
    mutationKey: [STU_QUERY_KEYS.CREATE_QUOTE],
    mutationFn: async (requestBody: IStuQuoteRequest) => {
      const url = `${STU_JOURNEY_API_BASE}/quotes`;
      const response = await ssoAxiosInstance.post<IStuCreateQuoteResponse>(
        url,
        requestBody,
      );
      return response.data;
    },
  };
};

export const getStuQuote = (quoteId: string) => {
  return {
    queryKey: [STU_QUERY_KEYS.GET_QUOTE, quoteId, STU_QUERY_KEYS.PERSIST],
    queryFn: async () => {
      const url = `${STU_JOURNEY_API_BASE}/quotes`;
      const response = await ssoAxiosInstance.get<IStuQuoteResponse>(url, {
        params: { quoteId },
      });
      return response.data;
    },
    enabled: !!quoteId,
    retry: retryOnTransientError,
    retryDelay: 1000,
    refetchInterval: (query: {
      state: { data?: IStuQuoteResponse; status?: string; error?: unknown };
    }) =>
      (query.state.status !== 'error' ||
        isTransientError(query.state.error)) &&
      query.state.data?.status === 'IN_PROGRESS'
        ? STU_TIMEOUT_VALUES.QUOTE_POLL_INTERVAL_MS
        : false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  };
};

export const getStuSelection = (quoteId: string, planId: string) => {
  return {
    queryKey: [
      STU_QUERY_KEYS.GET_SELECTION,
      quoteId,
      planId,
      STU_QUERY_KEYS.PERSIST,
    ],
    queryFn: async () => {
      const url = `${STU_JOURNEY_API_BASE}/selection`;
      const response = await ssoAxiosInstance.get<IStuSelection>(url, {
        params: { quoteId, planId },
      });
      return response.data;
    },
    enabled: !!quoteId && !!planId,
    retry: retryOnTransientError,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  };
};

export const updateStuSelection = () => {
  return {
    mutationKey: [STU_QUERY_KEYS.UPDATE_SELECTION],
    mutationFn: async (requestBody: IStuSelectionRequest) => {
      const url = `${STU_JOURNEY_API_BASE}/selection`;
      const response = await ssoAxiosInstance.post<IStuSelection>(
        url,
        requestBody,
      );
      return response.data;
    },
  };
};

export interface ISuperleapMemberDetailPayload {
  relationship: string;
  gender: string;
  dob: string;
}

export interface ISuperleapCreateOpportunityRequest {
  memberDetails: ISuperleapMemberDetailPayload[];
}

export type SuperleapOpportunityStatus = 'created' | 'failed';

export interface ISuperleapCreateOpportunityResponse {
  status: SuperleapOpportunityStatus;
  superleapLeadId: string | null;
  opportunityId: string | null;
  reason: string | null;
}

export const createSuperleapOpportunity = () => {
  return {
    mutationKey: [STU_QUERY_KEYS.CREATE_SUPERLEAP_OPPORTUNITY],
    mutationFn: async (
      requestBody: ISuperleapCreateOpportunityRequest,
    ): Promise<ISuperleapCreateOpportunityResponse> => {
      const url = `${STU_JOURNEY_API_BASE}/superleap/opportunity`;
      const response = await ssoAxiosInstance.post(url, requestBody);
      return response.data;
    },
  };
};

export const getStuJourneyList = (limit?: number) => {
  return {
    queryKey: [STU_QUERY_KEYS.GET_JOURNEY_LIST, limit ?? 'default'],
    queryFn: async () => {
      const url = `${STU_JOURNEY_API_BASE}/list`;
      const response = await ssoAxiosInstance.get<IStuJourneyList>(url, {
        params: limit ? { limit } : undefined,
      });
      return response.data;
    },
  };
};

export const commitStuSelection = () => {
  return {
    mutationKey: [STU_QUERY_KEYS.COMMIT_SELECTION],
    mutationFn: async (requestBody: IStuCommitRequest) => {
      const url = `${STU_JOURNEY_API_BASE}/selection/commit`;
      const response = await ssoAxiosInstance.post<IStuSelection>(
        url,
        requestBody,
      );
      return response.data;
    },
  };
};

export const getStuProposalQuestions = (quoteId: string, planId: string) => {
  return {
    queryKey: [
      STU_QUERY_KEYS.GET_PROPOSAL_QUESTIONS,
      quoteId,
      planId,
    ],
    queryFn: async () => {
      const url = `${STU_JOURNEY_API_BASE}/proposal/questions`;
      const response = await ssoAxiosInstance.get<IStuProposalForm>(url, {
        params: { quoteId, planId: Number(planId) },
      });
      return response.data;
    },
    enabled: !!quoteId && !!planId,
    retry: (failureCount: number, error: unknown) =>
      !isStuQuoteLocked(error) && !isStuQuoteExpired(error) && failureCount < 3,
    gcTime: 0,
    staleTime: 0,
  };
};

export const saveStuProposalAnswers = () => {
  return {
    mutationKey: [STU_QUERY_KEYS.SAVE_PROPOSAL_ANSWERS],
    mutationFn: async ({
      proposalId,
      ...requestBody
    }: IStuSaveAnswersRequest) => {
      const url = `${STU_JOURNEY_API_BASE}/proposal/${proposalId}/answers`;
      const response = await ssoAxiosInstance.patch<IStuSaveAnswersResponse>(
        url,
        requestBody,
      );
      return response.data;
    },
  };
};

// Two insurer calls sit behind an upload; the spec allows up to ~90 s, so outlast the relay.
const STU_DOCUMENT_UPLOAD_TIMEOUT_MS = 120_000;

export const uploadStuProposalDocument = () => {
  return {
    mutationKey: [STU_QUERY_KEYS.UPLOAD_PROPOSAL_DOCUMENT],
    mutationFn: async ({
      proposalId,
      ...requestBody
    }: IStuUploadDocumentRequest) => {
      const url = `${STU_JOURNEY_API_BASE}/proposal/${proposalId}/documents`;
      const response = await ssoAxiosInstance.post<IStuUploadDocumentResponse>(
        url,
        requestBody,
        { timeout: STU_DOCUMENT_UPLOAD_TIMEOUT_MS },
      );
      return response.data;
    },
  };
};

export const affirmStuProposal = () => {
  return {
    mutationKey: [STU_QUERY_KEYS.AFFIRM_PROPOSAL],
    mutationFn: async ({ proposalId, affirmedAnswersHash }: IStuAffirmRequest) => {
      const url = `${STU_JOURNEY_API_BASE}/proposal/${proposalId}/affirm`;
      const response = await ssoAxiosInstance.post<IStuAffirmResponse>(url, {
        affirmedAnswersHash,
      });
      return response.data;
    },
  };
};

// Every Proceed re-sends the proposal to the insurer; the guide allows up to ~90 s (2026-10-20).
const STU_SUBMIT_TIMEOUT_MS = 120_000;

export const submitStuProposal = () => {
  return {
    mutationKey: [STU_QUERY_KEYS.SUBMIT_PROPOSAL],
    mutationFn: async ({ proposalId, idempotencyKey }: IStuSubmitRequest) => {
      const url = `${STU_JOURNEY_API_BASE}/proposal/${proposalId}/submit`;
      const response = await ssoAxiosInstance.post<IStuSubmitResponse>(
        url,
        undefined,
        {
          headers: { 'Idempotency-Key': idempotencyKey },
          timeout: STU_SUBMIT_TIMEOUT_MS,
        },
      );
      return response.data;
    },
  };
};

export const getStuPaymentSummary = (quoteId: string, planId: string) => {
  return {
    queryKey: [STU_QUERY_KEYS.GET_PAYMENT_SUMMARY, quoteId, planId],
    queryFn: async () => {
      const url = `${STU_JOURNEY_API_BASE}/payment/summary`;
      const response = await ssoAxiosInstance.get<IStuPaymentSummary>(url, {
        params: { quoteId, planId: Number(planId) },
      });
      return response.data;
    },
    enabled: !!quoteId && !!planId,
    gcTime: 0,
    staleTime: 0,
  };
};

export const initiateStuPayment = () => {
  return {
    mutationKey: [STU_QUERY_KEYS.INITIATE_PAYMENT],
    mutationFn: async ({
      idempotencyKey,
      ...requestBody
    }: IStuPaymentInitiateRequest) => {
      const url = `${STU_JOURNEY_API_BASE}/payment/initiate`;
      const response = await ssoAxiosInstance.post<IStuPaymentInitiateResponse>(
        url,
        requestBody,
        { headers: { 'Idempotency-Key': idempotencyKey } },
      );
      return response.data;
    },
  };
};

export const getStuPaymentStatus = (quoteId: string, planId: string) => {
  return {
    queryKey: [STU_QUERY_KEYS.GET_PAYMENT_STATUS, quoteId, planId],
    queryFn: async () => {
      const url = `${STU_JOURNEY_API_BASE}/payment/status`;
      const response = await ssoAxiosInstance.get<IStuPaymentStatus>(url, {
        params: { quoteId, planId: Number(planId) },
      });
      return response.data;
    },
    enabled: !!quoteId && !!planId,
    retry: retryOnTransientError,
    retryDelay: 1000,
    gcTime: 0,
    staleTime: 0,
  };
};

export const getStuPolicyDocument = (quoteId: string, planId: string) => {
  return {
    mutationKey: [STU_QUERY_KEYS.GET_POLICY_DOCUMENT, quoteId, planId],
    mutationFn: async () => {
      const url = `${STU_JOURNEY_API_BASE}/policy/document`;
      const response = await ssoAxiosInstance.get<IStuPolicyDocument>(url, {
        params: { quoteId, planId: Number(planId) },
      });
      return response.data;
    },
  };
};

export const getStuKycForm = (quoteId: string, planId: string) => {
  return {
    queryKey: [STU_QUERY_KEYS.GET_KYC_FORM, quoteId, planId],
    queryFn: async () => {
      const url = `${STU_JOURNEY_API_BASE}/kyc/form`;
      const response = await ssoAxiosInstance.get<IStuKycForm>(url, {
        params: { quoteId, planId: Number(planId) },
      });
      return response.data;
    },
    enabled: !!quoteId && !!planId,
    retry: retryOnTransientError,
    retryDelay: 1000,
    gcTime: 0,
    staleTime: 0,
  };
};

const isCacheable = (cacheControl: unknown) =>
  /max-age=\d+/i.test(String(cacheControl ?? '')) &&
  !/no-store/i.test(String(cacheControl ?? ''));

export const getStuPincode = (pincode: string) => {
  return {
    queryKey: [STU_QUERY_KEYS.GET_PINCODE, pincode],
    queryFn: async (): Promise<IStuPincodeLookup> => {
      const url = `${STU_JOURNEY_API_BASE}/pincode`;
      const response = await ssoAxiosInstance.get<IStuPincodeResponse>(url, {
        params: { pincode },
      });
      return {
        ...response.data,
        cacheable: isCacheable(response.headers['cache-control']),
      };
    },
    // A day when the server allows it; a `no-store` answer is asked again next time.
    staleTime: ({ state }: { state: { data?: IStuPincodeLookup } }) =>
      state.data?.cacheable ? STU_TTL_VALUES.PINCODE : 0,
    gcTime: STU_TTL_VALUES.PINCODE,
    retry: false,
  };
};

export const getStuIfsc = (ifsc: string) => {
  return {
    queryKey: [STU_QUERY_KEYS.GET_IFSC, ifsc],
    queryFn: async (): Promise<IStuIfscLookup> => {
      const url = `${STU_JOURNEY_API_BASE}/ifsc`;
      const response = await ssoAxiosInstance.get<IStuIfscResponse>(url, {
        params: { ifsc },
      });
      return {
        ...response.data,
        cacheable: isCacheable(response.headers['cache-control']),
      };
    },
    // Same as the pincode: a day when the server allows it, asked again after a `no-store`.
    staleTime: ({ state }: { state: { data?: IStuIfscLookup } }) =>
      state.data?.cacheable ? STU_TTL_VALUES.IFSC : 0,
    gcTime: STU_TTL_VALUES.IFSC,
    retry: false,
  };
};

export const initiateStuKyc = () => {
  return {
    mutationKey: [STU_QUERY_KEYS.INITIATE_KYC],
    mutationFn: async (requestBody: IStuKycInitiateRequest) => {
      const url = `${STU_JOURNEY_API_BASE}/kyc/initiate`;
      const response = await ssoAxiosInstance.post<IStuKycInitiateResponse>(
        url,
        requestBody,
      );
      return response.data;
    },
  };
};

export const verifyStuKycOtp = () => {
  return {
    mutationKey: [STU_QUERY_KEYS.VERIFY_KYC_OTP],
    mutationFn: async (requestBody: IStuKycVerifyOtpRequest) => {
      const url = `${STU_JOURNEY_API_BASE}/kyc/verify-otp`;
      const response = await ssoAxiosInstance.post<IStuKycInitiateResponse>(
        url,
        requestBody,
      );
      return response.data;
    },
  };
};

export const getStuKycState = (quoteId: string, planId: string) => {
  return {
    queryKey: [STU_QUERY_KEYS.GET_KYC_STATE, quoteId, planId],
    queryFn: async () => {
      const url = `${STU_JOURNEY_API_BASE}/kyc/state`;
      const response = await ssoAxiosInstance.get<IStuKycStateResponse>(url, {
        params: { quoteId, planId: Number(planId) },
      });
      return response.data;
    },
    enabled: !!quoteId && !!planId,
    retry: retryOnTransientError,
    retryDelay: 1000,
    gcTime: 0,
    staleTime: 0,
  };
};

export const acceptStuKycPrice = () => {
  return {
    mutationKey: [STU_QUERY_KEYS.ACCEPT_KYC_PRICE],
    mutationFn: async (requestBody: IStuKycAcceptPriceRequest) => {
      const url = `${STU_JOURNEY_API_BASE}/kyc/accept-price`;
      const response = await ssoAxiosInstance.post<IStuKycAcceptPriceResponse>(
        url,
        requestBody,
      );
      return response.data;
    },
  };
};
